"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.upgradePageChildListsToTree = upgradePageChildListsToTree;
const fractional_indexing_1 = require("fractional-indexing");
const Y = __importStar(require("yjs"));
const page_document_maps_1 = require("./page-document-maps");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_tree_1 = require("./page-structure-tree");
// Orders without a random suffix: every client that upgrades the same page
// writes the same entries, so concurrent upgrades merge to one order.
const nextOrder = fractional_indexing_1.generateKeyBetween;
const CHILD_LIST_KEY = 'sys:children';
// The surface held its canvas elements as Boxed(Y.Map): { type, value }.
const SURFACE_ELEMENTS_KEY = 'prop:elements';
const FRAME_FLAVOUR = 'affine:frame';
const FRAME_MEMBERS_KEY = 'prop:childElementIds';
const FRAME_PRESENTATION_KEY = 'prop:presentationIndex';
// A group listed its members in a children map; a mind map's children map
// is its node layout and stays: its shapes belong to it through that map.
const ELEMENT_MEMBERS_KEY = 'children';
const GROUP_TYPE = 'group';
const ELEMENT_ORDER_KEY = 'index';
function childList(block) {
    if (!(block instanceof Y.Map))
        return [];
    const list = block.get(CHILD_LIST_KEY);
    const ids = list instanceof Y.Array ? list.toArray() : list;
    return Array.isArray(ids)
        ? ids.filter((id) => typeof id === 'string')
        : [];
}
function keysOf(value) {
    if (value instanceof Y.Map)
        return [...value.keys()];
    return typeof value === 'object' && value !== null ? Object.keys(value) : [];
}
/**
 * Upgrade step of a stored page document: the child lists of its blocks
 * become structure entries in part 0, in list order, and the lists go. The
 * page block is the root; a block listed twice stays under the first parent
 * the walk from the root reaches. A block in no list keeps its fields and
 * gets an unplaced entry: it stays off the tree as before. The canvas elements of a placed
 * surface move into the elements map of part 0, each with an entry on its
 * surface and its layer index as order. Membership becomes the entry's g:
 * a group first, else a frame, never for the shapes of a mind map, which
 * belong to it through its node map; the frame's presentation index
 * becomes its pi. Child lists, member lists, element indexes and the
 * surface element map go. A page with entries is left as it is.
 */
function upgradePageChildListsToTree(doc) {
    const blocks = (0, page_document_maps_1.pagePartBlocksMap)(doc);
    const structure = (0, page_document_maps_1.pageStructureMap)(doc);
    if (structure.size > 0)
        return;
    const listed = new Set();
    blocks.forEach(block => childList(block).forEach(id => listed.add(id)));
    const roots = [...blocks.keys()]
        .filter(id => !listed.has(id) && blocks.get(id)?.get('sys:flavour') === 'affine:page')
        .sort();
    doc.transact(() => {
        const entries = new Map();
        const place = (id, parentId, order) => {
            const block = blocks.get(id);
            const flavour = block?.get('sys:flavour');
            if (entries.has(id) || typeof flavour !== 'string')
                return false;
            entries.set(id, {
                k: flavour,
                p: parentId,
                o: order,
                s: page_document_maps_1.PAGE_DOCUMENT_PART_ID,
            });
            return true;
        };
        let rootOrder = null;
        const pending = [];
        for (const root of roots) {
            rootOrder = nextOrder(rootOrder, null);
            if (place(root, null, rootOrder))
                pending.push(root);
        }
        for (const parentId of pending) {
            let order = null;
            for (const childId of childList(blocks.get(parentId))) {
                order = nextOrder(order, null);
                if (place(childId, parentId, order))
                    pending.push(childId);
            }
        }
        let unplacedOrder = null;
        for (const id of [...blocks.keys()].sort()) {
            if (entries.has(id))
                continue;
            unplacedOrder = nextOrder(unplacedOrder, null);
            place(id, page_structure_tree_1.PAGE_TREE_UNPLACED_ID, unplacedOrder);
        }
        const elements = (0, page_document_maps_1.pagePartElementsMap)(doc);
        const memberLists = [];
        const mindmapShapes = new Set();
        for (const surfaceId of pending) {
            const surface = blocks.get(surfaceId);
            if (entries.get(surfaceId).k !== page_structure_entry_1.PAGE_SURFACE_FLAVOUR)
                continue;
            const boxed = surface.get(SURFACE_ELEMENTS_KEY);
            const value = boxed instanceof Y.Map ? boxed.get('value') : null;
            if (!(value instanceof Y.Map))
                continue;
            // Elements without a valid index keep their stored order after the rest.
            let order = null;
            const stored = [...value.entries()]
                .filter((pair) => pair[1] instanceof Y.Map &&
                typeof pair[1].get('type') === 'string' &&
                !entries.has(pair[0]))
                .sort(([a, x], [b, y]) => {
                const ox = x.get(ELEMENT_ORDER_KEY);
                const oy = y.get(ELEMENT_ORDER_KEY);
                if ((0, page_structure_entry_1.isFractionalIndex)(ox) !== (0, page_structure_entry_1.isFractionalIndex)(oy))
                    return (0, page_structure_entry_1.isFractionalIndex)(ox) ? -1 : 1;
                const byOrder = (0, page_structure_entry_1.isFractionalIndex)(ox) && (0, page_structure_entry_1.isFractionalIndex)(oy)
                    ? compare(ox, oy)
                    : 0;
                return byOrder || compare(a, b);
            });
            for (const [id, element] of stored) {
                const index = element.get(ELEMENT_ORDER_KEY);
                order = (0, page_structure_entry_1.isFractionalIndex)(index) ? index : nextOrder(order, null);
                const fields = element.clone();
                fields.delete(ELEMENT_ORDER_KEY);
                const type = element.get('type');
                const members = keysOf(element.get(ELEMENT_MEMBERS_KEY));
                if (type === GROUP_TYPE) {
                    fields.delete(ELEMENT_MEMBERS_KEY);
                    memberLists.push([id, members]);
                }
                else if (type === page_structure_entry_1.PAGE_MINDMAP_KIND) {
                    members.forEach(member => mindmapShapes.add(member));
                }
                elements.set(id, fields);
                entries.set(id, {
                    k: type,
                    p: surfaceId,
                    o: order,
                    s: page_document_maps_1.PAGE_DOCUMENT_PART_ID,
                });
            }
            surface.delete(SURFACE_ELEMENTS_KEY);
        }
        for (const [id, entry] of entries) {
            if (entry.k !== FRAME_FLAVOUR)
                continue;
            const frame = blocks.get(id);
            memberLists.push([id, keysOf(frame.get(FRAME_MEMBERS_KEY))]);
            const presentation = frame.get(FRAME_PRESENTATION_KEY);
            if ((0, page_structure_entry_1.isFractionalIndex)(presentation))
                entry.pi = presentation;
            frame.delete(FRAME_MEMBERS_KEY);
            frame.delete(FRAME_PRESENTATION_KEY);
        }
        // Groups come before frames: an entry has at most one g.
        for (const [groupId, members] of memberLists) {
            for (const member of members) {
                const entry = entries.get(member);
                if (entry &&
                    member !== groupId &&
                    entry.g === undefined &&
                    !mindmapShapes.has(member)) {
                    entry.g = groupId;
                }
            }
        }
        for (const [id, entry] of entries) {
            structure.set(id, (0, page_structure_entry_1.encodePageStructureEntry)(entry));
        }
        blocks.forEach(block => {
            if (block instanceof Y.Map)
                block.delete(CHILD_LIST_KEY);
        });
    });
}
// Code unit order like derivePageTree: fractional indexes are ASCII.
function compare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
